import { z } from "zod";

import type { DeepPartial } from "../../../../shared/core/common.types";
import { AdvancedSettingsSchema, DEFAULT_ADVANCED_SETTINGS } from "../Advanced.schema";
import { AssistantSettingsSchema, DEFAULT_ASSISTANT_SETTINGS } from "../Assistant.schema";
import { DEFAULT_RATE_LIMIT_SETTINGS } from "../RateLimit.schema";
import { EmbedSecuritySettingsSchema } from "./EmbedSecuritySettings.schema";
import { EmbedSettingsSchema } from "./EmbedSettings.schema";
import {
  DEFAULT_EMBED_VOICE_SETTINGS,
  embedVoiceSettingsFillFor,
  EmbedVoiceSettingsSchema
} from "./EmbedVoiceSettings.schema";

export const EmbedDeploymentSettingsSchema = z.object({
  embedSettings: EmbedSettingsSchema,
  securitySettings: EmbedSecuritySettingsSchema,
  assistantSettings: AssistantSettingsSchema,
  advancedSettings: AdvancedSettingsSchema,
  // Realtime "call the agent" voice config. Optional so embed deployments
  // created before this feature still parse; absent = voice disabled.
  voiceSettings: EmbedVoiceSettingsSchema.optional()
});

export type EmbedDeploymentSettings = z.infer<typeof EmbedDeploymentSettingsSchema>;

export const DEFAULT_EMBED_SETTINGS: EmbedDeploymentSettings = {
  embedSettings: {
    displayName: "Chat Assistant",
    welcomeMessages: ["Hi! How can I help you today?"],
    defaultLanguage: "en",
    supportedLanguages: ["en"],
    suggestedMessages: [],
    showTimestamp: false,
    bubblePosition: "bottom-right",
    bubbleBorderRadius: "lg",
    bubbleBackgroundColor: "#FFFFFF",
    bubbleBorderColor: "#FFFFFF",
    bubbleBorderWidth: 0,
    bubbleSize: "medium",
    uiAppearance: "light",
    uiRadius: "md",
    uiContainerRadius: "md",
    uiBgPattern: "",
    uiPrimaryColor: "#000000",
    uiAgentMessageColor: "#f3f4f6",
    uiAgentMessageTextColor: "#1f2937",
    uiUserMessageColor: "#3b82f6",
    uiUserMessageTextColor: "#ffffff",
    showHeader: true,
    headerMessage: "",
    showFooter: false,
    footerMessage: "",
    footerLinks: [],
    chatInputPlaceholder: "",
    landingScreenEnabled: false,
    landingScreenWelcomeMessage: "How can we help you?",
    landingScreenNewConversationLabel: "New Conversation",
    landingScreenNewConversationDescription: "Start a new chat with our team",
    landingScreenShowPastConversations: true,
    landingScreenSingleActiveConversation: false,
    landingScreenInactiveConversationThresholdHours: 3,
    landingScreenHidePastConversationsEnabled: false,
    landingScreenHidePastConversationsAfterDays: 30,
    landingScreenPastConversationsTitle: "",
    landingScreenChannelsTitle: "",
    landingScreenActionButtons: [],
    landingScreenShowFooter: false,
    landingScreenFooterMessage: "",
    landingScreenFooterLinks: []
  },
  securitySettings: {
    visibility: "public",
    allowOnlyOnSpecificDomains: false,
    allowedDomains: [],
    // Explicit `false` rather than an omission, so the console form's
    // `mergeEmbedDefaults` seeds a defined value and a legacy row round-trips
    // through Save as OFF instead of as absent. The backend treats absent and
    // `false` identically; this is about what a human sees in the form.
    allowAnonymousChatSessions: false,
    ...DEFAULT_RATE_LIMIT_SETTINGS
  },
  assistantSettings: DEFAULT_ASSISTANT_SETTINGS,
  advancedSettings: DEFAULT_ADVANCED_SETTINGS,
  voiceSettings: DEFAULT_EMBED_VOICE_SETTINGS
};

export function createEmbedDeploymentSettings(
  overrides?: DeepPartial<EmbedDeploymentSettings>
): EmbedDeploymentSettings {
  if (!overrides) return { ...DEFAULT_EMBED_SETTINGS };

  const merge = <T extends Record<string, unknown>>(base: T, override?: DeepPartial<T>): T => {
    if (!override) return base;
    const result = { ...base };
    for (const key in override) {
      if (override[key] !== undefined) {
        result[key] = override[key] as T[Extract<keyof T, string>];
      }
    }
    return result;
  };

  return {
    embedSettings: merge(DEFAULT_EMBED_SETTINGS.embedSettings, overrides.embedSettings),
    securitySettings: merge(DEFAULT_EMBED_SETTINGS.securitySettings, overrides.securitySettings),
    assistantSettings: merge(DEFAULT_EMBED_SETTINGS.assistantSettings, overrides.assistantSettings),
    advancedSettings: merge(DEFAULT_EMBED_SETTINGS.advancedSettings, overrides.advancedSettings),
    voiceSettings: merge(
      embedVoiceSettingsFillFor(overrides.voiceSettings),
      overrides.voiceSettings
    )
  };
}
